import classNames from 'classnames/bind';
import React from 'react';
import { RiSave3Line } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import styles from './Save.module.scss';

import actions from '@/actions';
import { FieldText } from '@/components/ui/kit/Field';
import { SAFETY_MARGIN } from '@/config';
import { getCommandForSurveySegment } from '@/helpers/CommandConverter';
import { postMission, patchMission } from '@/helpers/Requester';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const Save = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const name = useSelector((state) => state.mission.name);
  const specs = useSelector((state) => state.mission.json.specs);
  const collisions = useSelector((state) => state.mission.collisions);
  const commands = useSelector((state) => state.mission.json.commands);
  const fences = useSelector((state) => state.mission.json.fences);

  const validAll = () => {
    if (commands.length === 0) {
      notifier.error('No mission defined.');
      return false;
    }
    if (name.trim() === '') {
      notifier.error('Please enter the mission name.');
      return false;
    }
    if (collisions.some((collision) => collision !== null)) {
      notifier.error(`Please increase altitude to ensure ${SAFETY_MARGIN}m clearance.`);
      return false;
    }
    if (!validWithJumpToItem()) {
      notifier.error("Please check the item number in the 'Jump To Item'.");
      return false;
    }
    return true;
  };

  const doSave = () => {
    if (!validAll()) return;

    let request;
    if (params.id === 'new') {
      request = postMission(name, { specs, commands, fences });
    } else {
      request = patchMission(params.id, name, { specs, commands, fences });
    }

    request.then(({ success, data }) => {
      if (success) {
        if (params.id === 'new') {
          navigate(`/mission-hub/${data.id}`, { replace: true });
        }
        notifier.success('The mission have been saved.');
      }
      // 미션명 존재하는 경우
      else if (data.code === 2001) {
        notifier.error('The mission name already exists.');
      }
    });
  };

  const doSaveAs = () => {
    if (!validAll()) return;

    const json = {
      specs,
      commands: commands.map((command) => ({
        ...command,
        id: uuidv4(), // 각 미션 아이템 신규 ID 정의
      })),
    };

    postMission(name, json).then(({ success, data }) => {
      if (success) {
        navigate(`/mission-hub/${data.id}`, { replace: true });
        notifier.success('The mission have been saved with new name.');
      }
      // 미션명 존재하는 경우
      else if (data.code === 2001) {
        notifier.error('The mission name already exists.');
      }
    });
  };

  const handleChangeName = (e) => {
    dispatch(actions.mission.changeName(e.target.value));
  };

  const validWithJumpToItem = () => {
    if (!commands.some(({ type }) => type === 'doJump')) return true;

    let jumpToItemNumber = 1;
    let commandCount = 0;

    commands.forEach((command) => {
      switch (command.type) {
        case 'doJump':
          jumpToItemNumber = Math.max(jumpToItemNumber, command.data.item);
          break;

        case 'navLand':
          // Parachute 항목 체크한 경우
          if (command.data.parachute) {
            commandCount += 3; // Waypoint, Parachute Enable, Release 반영
          }
          break;

        case 'navWaypoint':
          // Speed 항목 체크한 경우
          if (command.data.speed) {
            commandCount++; // Change Speed 반영
          }
          break;

        case 'cusSurvey':
          {
            const surveyCommand = getCommandForSurveySegment(command);
            commandCount += surveyCommand.data.positions.length - 1;
          }
          break;

        default:
          break;
      }

      commandCount++;
    });

    return jumpToItemNumber <= commandCount;
  };

  return (
    <div className={cx('container')}>
      <FieldText value={name} maxLength={24} placeholder="Name" className={cx('field')} onChange={handleChangeName} />
      <button type="button" className={cx('button')} onClick={doSave}>
        <RiSave3Line size={16} color="white" />
      </button>
      <button type="button" className={cx('button')} onClick={doSaveAs}>
        <RiSave3Line size={16} color="white" />
        <div className={cx('as')}>As</div>
      </button>
    </div>
  );
};

export default Save;
